import {
  GlobalEvents,
  QueryStore,
  useGlobalEvent
} from "./chunk-25IAV6Y3.js";
import {
  EncryptedJsonStore,
  GlobalState,
  GlobalStore,
  InterProcessEvents,
  InterProcessStore,
  StoreSyncManager,
  drainIpcFanoutTally,
  startIpcFanoutTally,
  stopIpcFanoutTally,
  stripInvokeTransportPrefix
} from "./chunk-XVNHYOIL.js";
import "./chunk-QL34RJ4Y.js";
import {
  createRendererTransport,
  getOID,
  isMain,
  trustedIpcMain
} from "./chunk-3HMZ6FL6.js";
import {
  isRenderer
} from "./chunk-A6K3NLMV.js";
import {
  __require
} from "./chunk-DGUM43GV.js";

// src/lib/Broadcast.ts
function getApi() {
  if (typeof window === "undefined") return null;
  return window.ipc?.broadcast ?? null;
}
function createBroadcastProducer(topic) {
  const api = getApi();
  if (api) api.registerProducer(topic);
  let disposed = false;
  return {
    publish: (data) => {
      if (disposed) return;
      api?.publish(topic, data);
    },
    dispose: () => {
      if (disposed) return;
      disposed = true;
      api?.unregisterProducer(topic);
    }
  };
}
function subscribeBroadcast(topic, callback) {
  const api = getApi();
  if (!api) return () => {
  };
  api.registerConsumer(topic);
  const unsub = api.subscribe(topic, (data) => callback(data));
  let unsubscribed = false;
  return () => {
    if (unsubscribed) return;
    unsubscribed = true;
    unsub();
    api.unregisterConsumer(topic);
  };
}

// src/lib/BroadcastMain.ts
var BroadcastMain = class {
  static consumerRegistry = null;
  static registerConsumerRegistry(registry) {
    this.consumerRegistry = registry;
  }
  static clearConsumerRegistry(registry) {
    if (!registry || this.consumerRegistry === registry) {
      this.consumerRegistry = null;
    }
  }
  static publish(topic, data) {
    for (const consumer of this.consumerRegistry?.getConsumers(topic) ?? []) {
      if (consumer.isDestroyed()) continue;
      try {
        consumer.postMessage("broadcast:main-message", { topic, data });
      } catch (err) {
        console.error("[BroadcastMain] publish failed for topic:", topic, err);
      }
    }
  }
};
function publishBroadcastFromMain(topic, data) {
  BroadcastMain.publish(topic, data);
}

// src/lib/BroadcastBroker.ts
var BroadcastBroker = class {
  producers = /* @__PURE__ */ new Map();
  consumers = /* @__PURE__ */ new Map();
  channels = /* @__PURE__ */ new Map();
  /** Latest preload lifecycle id seen for each WebContents id. */
  lifecycleIds = /* @__PURE__ */ new Map();
  cleanupBoundWebContentsIds = /* @__PURE__ */ new Set();
  started = false;
  createChannel;
  constructor(options) {
    this.createChannel = options?.createChannel ?? (() => {
      const { MessageChannelMain } = __require("electron");
      return new MessageChannelMain();
    });
  }
  start() {
    if (this.started) return;
    this.started = true;
    if (isRenderer || !isMain) {
      throw new Error("BroadcastBroker can only be started from the Electron main process");
    }
    BroadcastMain.registerConsumerRegistry(this);
    trustedIpcMain.on(
      "broadcast:register",
      (event, topic, role, lifecycleId) => {
        const wc = event.sender;
        if (typeof topic !== "string" || role !== "producer" && role !== "consumer") {
          return;
        }
        try {
          this.refreshLifecycle(wc, lifecycleId);
          if (role === "producer") {
            this.attachProducer(topic, wc);
          } else {
            this.attachConsumer(topic, wc);
          }
        } catch (err) {
          console.error("[BroadcastBroker] register failed:", err);
        }
      }
    );
    trustedIpcMain.on(
      "broadcast:unregister",
      (event, topic, role, lifecycleId) => {
        const wc = event.sender;
        if (typeof topic !== "string") return;
        try {
          if (typeof lifecycleId === "string" && this.lifecycleIds.get(wc.id) !== lifecycleId) return;
          if (role === "producer") {
            this.detachProducer(topic, wc);
          } else if (role === "consumer") {
            this.detachConsumer(topic, wc);
          }
        } catch (err) {
          console.error("[BroadcastBroker] unregister failed:", err);
        }
      }
    );
  }
  attachProducer(topic, wc) {
    let set = this.producers.get(topic);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.producers.set(topic, set);
    }
    if (set.has(wc)) return;
    set.add(wc);
    this.bindCleanup(wc);
    for (const consumer of this.consumers.get(topic) ?? []) {
      this.connect(topic, wc, consumer);
    }
  }
  attachConsumer(topic, wc) {
    let set = this.consumers.get(topic);
    if (!set) {
      set = /* @__PURE__ */ new Set();
      this.consumers.set(topic, set);
    }
    if (set.has(wc)) return;
    set.add(wc);
    this.bindCleanup(wc);
    for (const producer of this.producers.get(topic) ?? []) {
      this.connect(topic, producer, wc);
    }
  }
  detachProducer(topic, wc) {
    this.producers.get(topic)?.delete(wc);
    this.closeChannelPairsForWebContents(wc.id, topic, "producer");
  }
  detachConsumer(topic, wc) {
    this.consumers.get(topic)?.delete(wc);
    this.closeChannelPairsForWebContents(wc.id, topic, "consumer");
  }
  getConsumers(topic) {
    return Array.from(this.consumers.get(topic) ?? []);
  }
  bindCleanup(wc) {
    if (this.cleanupBoundWebContentsIds.has(wc.id)) return;
    this.cleanupBoundWebContentsIds.add(wc.id);
    wc.once("destroyed", () => {
      this.lifecycleIds.delete(wc.id);
      this.cleanupBoundWebContentsIds.delete(wc.id);
      this.removeWebContents(wc);
    });
  }
  refreshLifecycle(wc, lifecycleId) {
    if (typeof lifecycleId !== "string" || lifecycleId.length === 0) return;
    const previousLifecycleId = this.lifecycleIds.get(wc.id);
    if (previousLifecycleId === lifecycleId) return;
    if (previousLifecycleId) {
      this.removeWebContents(wc);
    }
    this.lifecycleIds.set(wc.id, lifecycleId);
  }
  removeWebContents(wc) {
    for (const set of this.producers.values()) {
      set.delete(wc);
    }
    for (const set of this.consumers.values()) {
      set.delete(wc);
    }
    this.lifecycleIds.delete(wc.id);
    this.closeChannelPairsForWebContents(wc.id);
  }
  closeChannelPairsForWebContents(webContentsId, topic, role) {
    const entries = topic ? [[topic, this.channels.get(topic)]] : this.channels.entries();
    for (const [entryTopic, pairs] of entries) {
      if (!pairs) continue;
      for (const [pairKey, pair] of pairs) {
        const matchesProducer = role !== "consumer" && pair.producer.id === webContentsId;
        const matchesConsumer = role !== "producer" && pair.consumer.id === webContentsId;
        if (matchesProducer || matchesConsumer) {
          this.closePair(entryTopic, pairKey, pair);
          pairs.delete(pairKey);
        }
      }
    }
  }
  connect(topic, producer, consumer) {
    if (producer === consumer) return;
    if (producer.isDestroyed() || consumer.isDestroyed()) return;
    const pairKey = `${producer.id}|${consumer.id}`;
    let pairs = this.channels.get(topic);
    if (!pairs) {
      pairs = /* @__PURE__ */ new Map();
      this.channels.set(topic, pairs);
    }
    if (pairs.has(pairKey)) return;
    pairs.set(pairKey, { producer, consumer });
    const channel = this.createChannel();
    let producerPortSent = false;
    let consumerPortSent = false;
    try {
      producer.postMessage("broadcast:producer-port", { topic, pairKey }, [channel.port1]);
      producerPortSent = true;
      consumer.postMessage("broadcast:consumer-port", { topic, pairKey }, [channel.port2]);
      consumerPortSent = true;
    } catch (err) {
      console.error("[BroadcastBroker] postMessage failed for topic:", topic, err);
      pairs.delete(pairKey);
      if (!producerPortSent) channel.port1.close();
      if (!consumerPortSent) channel.port2.close();
      this.closePair(topic, pairKey, {
        producer: producerPortSent ? producer : void 0,
        consumer: consumerPortSent ? consumer : void 0
      });
    }
  }
  closePair(topic, pairKey, pair) {
    if (pair.producer && !pair.producer.isDestroyed()) {
      this.closePort(pair.producer, topic, pairKey);
    }
    if (pair.consumer && !pair.consumer.isDestroyed()) {
      this.closePort(pair.consumer, topic, pairKey);
    }
  }
  closePort(wc, topic, pairKey) {
    try {
      wc.postMessage("broadcast:close-port", { topic, pairKey });
    } catch (err) {
      console.error("[BroadcastBroker] close port failed for topic:", topic, err);
    }
  }
};

// src/lib/snapshot-protocol.ts
var SNAPSHOT_EVENT_SUFFIX = ":get-snapshot";
function getSnapshotEventName(topic) {
  return `${topic}${SNAPSHOT_EVENT_SUFFIX}`;
}

// src/lib/snapshot-broadcaster.ts
var defaultTransport = {
  handle: (event, getSnapshot) => {
    GlobalEvents.handle(event, getSnapshot);
  },
  publish: (topic, snapshot) => {
    publishBroadcastFromMain(topic, snapshot);
  }
};
var installedSnapshotTopics = /* @__PURE__ */ new Set();
function createSnapshotBroadcaster({
  topic,
  initialSnapshot,
  transport = defaultTransport
}) {
  let snapshot = initialSnapshot;
  let installed = false;
  const getSnapshot = () => snapshot;
  const publish = (next) => {
    snapshot = {
      ...next,
      revision: snapshot.revision + 1
    };
    transport.publish(topic, snapshot);
    return snapshot;
  };
  const update = (updater) => {
    return publish(updater(snapshot));
  };
  const install = () => {
    if (installed) {
      return;
    }
    if (installedSnapshotTopics.has(topic)) {
      throw new Error(`Snapshot broadcaster already installed for topic "${topic}"`);
    }
    transport.handle(getSnapshotEventName(topic), async () => snapshot);
    installedSnapshotTopics.add(topic);
    installed = true;
  };
  return {
    getSnapshot,
    publish,
    update,
    install
  };
}

// src/lib/snapshot-consumer.ts
var defaultTransport2 = {
  subscribe: (topic, callback) => subscribeBroadcast(topic, callback),
  getSnapshot: (event) => GlobalEvents.invoke(event)
};
var activeSnapshotConsumers = /* @__PURE__ */ new Map();
function registerActiveConsumer(topic) {
  const activeCount = activeSnapshotConsumers.get(topic) ?? 0;
  if (activeCount > 0 && process.env.NODE_ENV === "development") {
    console.warn(
      `Multiple active snapshot consumers for topic "${topic}". Share one module-scoped consumer per renderer.`
    );
  }
  activeSnapshotConsumers.set(topic, activeCount + 1);
}
function unregisterActiveConsumer(topic) {
  const activeCount = activeSnapshotConsumers.get(topic);
  if (!activeCount || activeCount === 1) {
    activeSnapshotConsumers.delete(topic);
    return;
  }
  activeSnapshotConsumers.set(topic, activeCount - 1);
}
function createSnapshotConsumer({
  topic,
  initialSnapshot,
  onError,
  transport = defaultTransport2
}) {
  let snapshot = initialSnapshot;
  let unsubscribeBroadcast = null;
  const listeners = /* @__PURE__ */ new Set();
  const applySnapshot = (next) => {
    if (!next || next.revision <= snapshot.revision) {
      return;
    }
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  const refresh = async () => {
    try {
      applySnapshot(await transport.getSnapshot(getSnapshotEventName(topic)));
    } catch (error) {
      onError(error);
    }
  };
  const start = () => {
    registerActiveConsumer(topic);
    try {
      unsubscribeBroadcast = transport.subscribe(topic, applySnapshot);
    } catch (error) {
      unregisterActiveConsumer(topic);
      throw error;
    }
    void refresh();
  };
  const subscribe = (listener) => {
    const notify = () => listener();
    listeners.add(notify);
    if (listeners.size === 1) {
      try {
        start();
      } catch (error) {
        listeners.delete(notify);
        throw error;
      }
    }
    let subscribed = true;
    return () => {
      if (!subscribed) {
        return;
      }
      subscribed = false;
      listeners.delete(notify);
      if (listeners.size === 0) {
        try {
          unsubscribeBroadcast?.();
        } finally {
          unsubscribeBroadcast = null;
          unregisterActiveConsumer(topic);
        }
      }
    };
  };
  return {
    subscribe,
    getSnapshot: () => snapshot,
    refresh
  };
}
export {
  BroadcastBroker,
  BroadcastMain,
  EncryptedJsonStore,
  GlobalEvents,
  GlobalState,
  GlobalStore,
  InterProcessEvents,
  InterProcessStore,
  QueryStore,
  StoreSyncManager,
  createBroadcastProducer,
  createRendererTransport,
  createSnapshotBroadcaster,
  createSnapshotConsumer,
  drainIpcFanoutTally,
  getOID,
  getSnapshotEventName,
  isRenderer,
  publishBroadcastFromMain,
  startIpcFanoutTally,
  stopIpcFanoutTally,
  stripInvokeTransportPrefix,
  subscribeBroadcast,
  useGlobalEvent
};
//# sourceMappingURL=index.js.map